import type { Product } from '~/types/products'
import { defineStore } from 'pinia'
import { useNuxtApp } from '#app'
import { baseStore } from '~/stores/Base'
import type { LlanaListResponse, LlanaWhere, LlanaSocketData, LlanaListOptions } from '~/types/llana'
import { useProductCategoriesStore } from '~/stores/product/Categories'
import { type Social } from '~/types/common'

function defaultList(): LlanaListResponse<Product> {
	return {
		data: [],
		total: 0,
		limit: default_limit,
		offset: 0,
	}
}

const table_name = 'product'
type T = Product
const default_sort = 'created_at.desc'
const default_limit = 20
const default_relations = 'product_categories,users,product_available_at,product_socials'

export class ProductsStore extends baseStore<T> {
	constructor() {
		super({
			table_name,
			store_name: table_name,
			default_sort,
			default_limit,
			default_relations,
		})
	}
}

export const useProductsStore = () =>
	defineStore(table_name, {
		state: () => <LlanaListResponse<T>>defaultList(),
		actions: {
			async bindEvents(where?: { [key: string]: string | number }) {
				const { $llanaSubscribe } = useNuxtApp()
				await $llanaSubscribe(
					table_name,
					(data: LlanaSocketData) => this.get(data.id as number, where),
					(data: LlanaSocketData) => this.get(data.id as number, where),
					(data: LlanaSocketData) => this.remove(data.id as number, where),
				)
			},
			async create(data: T) {
				const store = new ProductsStore()
				const { state, record } = await store.create(this.$state, data)
				this.$state = state
				return record
			},
			async get(id: number, where?: { [key: string]: string | number }): Promise<T | undefined> {
				const store = new ProductsStore()
				const { state, record } = await store.get(this.$state, id, where)
				this.$state = state
				return record
			},
			async list(options?: LlanaListOptions): Promise<LlanaListResponse<T>> {
				const store = new ProductsStore()
				this.$state = await store.list(this.$state, options)
				return this.$state
			},
			async search(options?: {
				fields?: string[]
				limit?: number
				offset?: number
				where?: LlanaWhere[]
				relations?: string
				page?: string
				sort?: string
			}): Promise<LlanaListResponse<T>> {
				const store = new ProductsStore()
				return await store.search(options)
			},
			async reload(): Promise<LlanaListResponse<T>> {
				const store = new ProductsStore()
				this.$state = await store.reload()
				return this.$state
			},
			async update(id: number, data: Partial<T>): Promise<T> {
				const store = new ProductsStore()
				const { state, record } = await store.update(this.$state, id, data)
				this.$state = state
				return record
			},
			async remove(id: number, where?: { [key: string]: string | number }): Promise<void> {
				const store = new ProductsStore()
				this.$state = await store.remove(this.$state, id, where)
			},
			async delete(id: number): Promise<void> {
				const store = new ProductsStore()
				this.$state = await store.delete(this.$state, id)
			},
			async getByHandle(handle: string): Promise<Product | undefined> {
				const product = this.$state.data.find((p: Product) => p.handle === handle)
				if (product) {
					return product
				}

				const results = await this.search({
					where: [{ field: 'handle', value: handle, operator: 'equals' }],
					limit: 1,
				})

				if (results.data.length > 0) {
					const record = results.data[0]
					const index = this.$state.data.findIndex((p: Product) => p.id === record.id)
					if (index > -1) {
						this.$state.data[index] = record
					} else {
						this.$state.data.push(record)
					}
					return record
				}

				return
			},
			getURL(product: Product) {
				const categoryStore = useProductCategoriesStore()
				categoryStore().list() // Load categories
				const slug = categoryStore().getCategorySlug(product.category_id)
				return `/products/${slug}/product/${product.handle}`
			},
			async addSocial(data: Partial<Social>): Promise<Social> {
				const { $llana } = useNuxtApp()
				const result = await $llana<Social>({
					type: 'CREATE',
					table: 'product_socials',
					data,
				})
				const index = this.$state.data.findIndex((p: Product) => p.id === data.product_id)
				if (!this.$state.data[index].product_socials) {
					this.$state.data[index].product_socials = []
				}
				this.$state.data[index].product_socials.push(result as any)
				return result as Social
			},
			async deleteSocial(social: Social): Promise<void> {
				const { $llana } = useNuxtApp()
				await $llana<Social>({
					type: 'DELETE',
					table: 'product_socials',
					id: social.id,
				})
				const p_index = this.$state.data.findIndex((p: Product) => p.id === social.product_id)

				if (this.$state.data[p_index].product_socials) {
					const index = this.$state.data[p_index].product_socials?.findIndex(s => s.id === social.id)
					if (index > -1) {
						this.$state.data[p_index].product_socials.splice(index, 1)
					}
				}
			},
		},
	})
